import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import { useSlugFlow } from "hooks/useSlugFlow";
import SoftInput from "components/SoftInput";
import DataTable from "cxfcomponents/Tables/DataTable";
import SoftTypography from "components/SoftTypography";
import StatusBadge from "cxfcomponents/StatusBadge";
import { useMemo, useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { formatCredentialApplicationType } from "helpers/credentials";
import UsersCell from "layouts/Flows/components/UsersCell";
import defaultCredentialIcon from "assets/images/credential.svg";
import { useSelector } from "react-redux";
import { useCredentials } from "hooks/useCredentials";
import { AddCredentialDialog } from "../credentials/addCredentialDialog";
import { useAllowedPermissions } from "hooks/useAllowedPermissions";
import { PERMISSIONS } from "constants/portal";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import CredentialDialogForm from "layouts/pages/credential-management/components/CredentialDialogForm";
import { useNavigate } from "react-router-dom";
import { getResourceDependenciesWithHop } from "api/ResourceDependenciesApi";

export const Credentials = () => {
  const { user } = useAuth0();
  const { flow } = useSlugFlow();
  const [searchName, setSearchName] = useState("");
  const navigate = useNavigate();
  const { users } = useSelector((store) => store?.portal?.users);
  const { credentials } = useCredentials();
  const [flowCredentialIds, setFlowCredentialIds] = useState([]);
  const [openAddCredentialDialog, setOpenAddCredentialDialog] = useState(false);
  const { isAllowedAction } = useAllowedPermissions();
  const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(flow?.id);

  const determineShareStatus = (owner_id, share_user) => {
    const isShared = share_user?.some((uId) => uId === user?.cxfUserId);
    const isOwner = owner_id === user?.cxfUserId;
    if (isOwner) return "Owner";
    if (isShared) return "Shared";
    return "";
  };

  const determineOwnerSharedUsers = (share_user, owner_id) => {
    if (share_user && users) {
      const share_user_ids = share_user?.map((sUser) => sUser?.user_id);
      const sharedUsersAndOwnerData = users?.filter(
        (user) => share_user_ids?.includes(user.auth0_id) || user.auth0_id === owner_id
      );

      return sharedUsersAndOwnerData;
    }
  };

  const handleOpenAddCredentialsDialog = () => {
    setOpenAddCredentialDialog(true);
  };

  const handleFilterCredentials = (credential) => {
    return credential.application?.name?.toLowerCase()?.includes(searchName.toLowerCase());
  };

  const tableData = useMemo(() => {
    const filteredCredentials = credentials.filter((credential) =>
      flowCredentialIds.includes(credential.id)
    );

    return {
      columns: [
        {
          Header: "icon",
          accessor: "icon",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => <SoftBox width="60px">{value}</SoftBox>
        },
        {
          Header: "Name",
          accessor: "name",
          disableSortBy: false,
          Cell: ({ value }) => (
            <SoftTypography
              sx={{ color: "#2D3748" }}
              className="white-to-dark"
              fontSize="14px"
              fontWeight="bold"
            >
              {value}
            </SoftTypography>
          )
        },
        {
          Header: "Type",
          accessor: "type",
          disableSortBy: false,
          Cell: ({ value }) => (
            <SoftTypography fontSize="12px" fontWeight="bold">
              {value}
            </SoftTypography>
          )
        },
        {
          Header: "Status",
          accessor: "status",
          disableSortBy: false,
          Cell: ({ value }) => <StatusBadge status={value} />
        },
        {
          Header: "Role",
          accessor: "role",
          disableSortBy: false,
          Cell: ({ value }) => (
            <SoftTypography fontSize="12px" fontWeight="bold">
              {value}
            </SoftTypography>
          )
        },
        {
          Header: "Expires",
          accessor: "expires",
          disableSortBy: false
        },
        {
          Header: "Users",
          accessor: "users",
          disableSortBy: true,
          Cell: ({ value }) => <UsersCell users={value} />
        },
        {
          Header: "Created",
          accessor: "created",
          disableSortBy: false,
          disableGlobalFilter: true,
          Cell: ({ value }) => (
            <SoftTypography fontSize="12px">
              {value}
            </SoftTypography>
          )
        },
        {
          Header: "Last updated",
          accessor: "last_updated",
          disableSortBy: false,
          Cell: ({ value }) => (
            <SoftTypography fontSize="12px">
              {value}
            </SoftTypography>
          )
        },
      ].map((column) => ({ ...column, disableResize: true })),
      rows: [
        ...filteredCredentials.filter(handleFilterCredentials).map((credential) => {
          const { application, share_user, owner_id } = credential;
          return {
            name: (
              <SoftTypography
                variant="text"
                onClick={() => navigate(`/credential/${credential.id}`)}
                sx={{ cursor: "pointer" }}
              >
                {application?.name}
              </SoftTypography>
            ),
            type: application?.applicationType
              ? formatCredentialApplicationType(application?.applicationType)
              : application?.applicationType,
            status: "active",
            role: determineShareStatus(owner_id, share_user),
            users: determineOwnerSharedUsers(share_user, owner_id) || [],
            created: credential.create_date_time
              ? formatDateAndTime(getLocalDateTimeString(credential.create_date_time))
              : "",
            last_updated: credential.updated_date_time
              ? formatDateAndTime(getLocalDateTimeString(credential.updated_date_time))
              : "",
            icon: (
              <SoftBox
                component="img"
                width="100%"
                src={
                  credential?.logo
                    ? `${credential.logo}?v=${credential.updated_date_time ?? ""}`
                    : defaultCredentialIcon
                }
              />
            )
          };
        })
      ]
    };
  }, [
    credentials,
    flowCredentialIds,
    users,
    searchName,
    isReadOnlyAccess,
    user?.cxfUserId
  ]);

  useEffect(() => {
    const fetchFlowCredentialDependencies = async () => {
      if (!user?.stripeId || !flow?.id) {
        setFlowCredentialIds([]);
        return;
      }

      try {
        const dependenciesResponse = await getResourceDependenciesWithHop({
          tenantId: user.stripeId,
          sourceId: flow.id,
          direction: "forward",
          directTargetResourceType: "credential",
          throughTargetResourceType: "connector",
          throughDirection: "forward",
          finalTargetResourceType: "credential",
          contextIdPrefix: flow.id,
          targetIdFromContextId: false
        });
        const credentialIds = Array.isArray(dependenciesResponse?.resultObject)
          ? dependenciesResponse.resultObject.map(
              (dependency) => dependency.target_id
            )
          : [];

        setFlowCredentialIds([...new Set(credentialIds)]);
      } catch (error) {
        console.error(error);
        setFlowCredentialIds([]);
      }
    };

    fetchFlowCredentialDependencies();
  }, [user?.stripeId, flow?.id]);

  return (
    <>
      <SoftBox>
        <SoftBox
          sx={{
            alignItems: "flex-start",
            display: "flex",
            justifyContent: "flex-start",
            padding: "12px 0 12px 40px"
          }}
        >
          <SoftInput
            size="medium"
            placeholder="Search..."
            value={searchName}
            onChange={({ currentTarget }) => {
              setSearchName(currentTarget.value);
            }}
            sx={{
              maxWidth: "380px",
              marginRight: "34px"
            }}
          />
          {isAllowedAction(flow?.created_by_id, [
            PERMISSIONS.EDIT_WORKFLOWS,
            PERMISSIONS.EDIT_ANY_WORKFLOWS
          ]) &&
            !isReadOnlyAccess && (
              <SoftButton variant="outlined" color="info" onClick={handleOpenAddCredentialsDialog}>
                add
              </SoftButton>
            )}
        </SoftBox>
        <SoftBox>
          <DataTable
            table={tableData}
            entriesPerPage={{ defaultValue: 25 }}
            hiddenTopButtons
            tableView={"list"}
            tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))"
          />
        </SoftBox>
      </SoftBox>
      <AddCredentialDialog
        openAddCredentialDialog={openAddCredentialDialog}
        onClose={() => setOpenAddCredentialDialog(false)}
        credentials={credentials}
      />
      <CredentialDialogForm />
    </>
  );
};
